import React, { useState, useEffect } from 'react';
import { getJobSummary, getJobDetail, getInventories, exportJobSummary, createAdjustment, getAdjustments, getSettings } from '../lib/api';
import { downloadBlob } from '../lib/downloadHelper';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Briefcase,
  Search,
  Loader2,
  DollarSign,
  Package,
  Calendar,
  ChevronUp,
  ChevronDown,
  Eye,
  Download,
  Printer,
  Edit3,
  History,
  Filter
} from 'lucide-react';

const JobSummary = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInventory, setSelectedInventory] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('last_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Adjustment state
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustItem, setAdjustItem] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [adjustError, setAdjustError] = useState('');

  // Adjustment history
  const [historyOpen, setHistoryOpen] = useState(false);
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [taxRate, setTaxRate] = useState(0);

  useEffect(() => {
    getInventories().then(res => setInventories(res.data)).catch(() => {});
    getSettings().then(res => setTaxRate(res.data.sales_tax_rate || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [selectedInventory, startDate, endDate]);

  useEffect(() => {
    const t = setTimeout(() => fetchJobs(), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.job_number = searchQuery;
      if (selectedInventory !== 'all') params.inventory_id = selectedInventory;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await getJobSummary(params);
      setJobs(res.data);
    } catch (err) {
      toast.error('Failed to load job summary');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (job) => {
    setSelectedJob(job);
    setDetailOpen(true);
    setLoadingDetail(true);
    try {
      const res = await getJobDetail(job.job_number);
      setJobDetail(res.data);
    } catch (err) {
      toast.error('Failed to load job details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (searchQuery) params.job_number = searchQuery;
      if (selectedInventory !== 'all') params.inventory_id = selectedInventory;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await exportJobSummary(params);
      downloadBlob(res.data, `job_cost_summary_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => window.print();

  const openAdjust = (item) => {
    setAdjustItem(item);
    setAdjustQty(item.quantity.toString());
    setAdjustNotes('');
    setAdjustError('');
    setAdjustOpen(true);
  };

  const submitAdjustment = async () => {
    const newQty = parseInt(adjustQty);
    if (isNaN(newQty) || newQty < 0) {
      setAdjustError('Enter a valid quantity (0 or more)');
      return;
    }
    if (newQty === adjustItem.quantity) {
      setAdjustError('Quantity unchanged');
      return;
    }
    setAdjusting(true);
    setAdjustError('');
    try {
      await createAdjustment({
        original_transaction_id: adjustItem.transaction_id,
        new_quantity: newQty,
        notes: adjustNotes || `Adjusted: ${adjustItem.quantity} -> ${newQty}`
      });
      setAdjustOpen(false);
      // Refresh job detail
      if (selectedJob) {
        const res = await getJobDetail(selectedJob.job_number);
        setJobDetail(res.data);
      }
      fetchJobs();
    } catch (err) {
      setAdjustError(err.response?.data?.detail || 'Adjustment failed');
    } finally {
      setAdjusting(false);
    }
  };

  const openHistory = async (transactionId) => {
    setHistoryOpen(true);
    setLoadingHistory(true);
    try {
      const res = await getAdjustments(transactionId);
      setAdjustmentHistory(res.data);
    } catch (err) {
      toast.error('Failed to load adjustment history');
    } finally {
    }
  };

  const sortedJobs = [...jobs].sort((a, b) => {
    let aVal, bVal;
    if (sortBy === 'total_cost') {
      aVal = a.total_cost; bVal = b.total_cost;
    } else if (sortBy === 'item_count') {
      aVal = a.item_count; bVal = b.item_count;
    } else if (sortBy === 'job_number') {
      aVal = a.job_number.toLowerCase(); bVal = b.job_number.toLowerCase();
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    } else {
      aVal = a.last_date; bVal = b.last_date;
    }
    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const totalCost = jobs.reduce((sum, j) => sum + j.total_cost, 0);
  const totalTax = totalCost * (taxRate / 100);
  const totalWithTax = totalCost + totalTax;

  const handleSort = (col) => {
    if (sortBy === col) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortOrder('desc'); }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="job-summary-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Job Cost Summary</h1>
          <p className="text-[#6B7280]">Track material costs per job for profitability analysis</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button
            onClick={handleExport}
            disabled={exporting || jobs.length === 0}
            className="bg-green-600 hover:bg-green-700 text-white rounded-lg gap-2"
            data-testid="export-jobs-btn"
          >
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Export Excel
          </Button>
          <Button
            onClick={handlePrint}
            variant="outline"
            className="rounded-lg gap-2 border-[#0776BD] text-[#0776BD]"
            data-testid="print-jobs-btn"
          >
            <Printer size={16} />
            Print
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={`grid grid-cols-1 gap-4 ${taxRate > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#E6F2FA] rounded-xl">
                <Briefcase size={24} className="text-[#0776BD]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Total Jobs</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]" data-testid="total-jobs-count">{jobs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#FFF8E1] rounded-xl">
                <DollarSign size={24} className="text-[#F0C500]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Subtotal</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]" data-testid="total-material-cost">${totalCost.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {taxRate > 0 && (
          <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-xl">
                  <DollarSign size={24} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Tax ({taxRate}%)</p>
                  <p className="text-3xl font-bold font-heading text-[#1F2937]" data-testid="total-tax">${totalTax.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-xl">
                <Package size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">{taxRate > 0 ? 'Total After Tax' : 'Avg Cost / Job'}</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]">
                  ${taxRate > 0 ? totalWithTax.toFixed(2) : (jobs.length > 0 ? (totalCost / jobs.length).toFixed(2) : '0.00')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={20} />
              <Input
                placeholder="Search by job number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="search-jobs-input"
              />
            </div>
            <Select value={selectedInventory} onValueChange={setSelectedInventory}>
              <SelectTrigger className="w-full md:w-[200px] h-12 rounded-lg border-gray-300" data-testid="job-inventory-filter">
                <SelectValue placeholder="All Inventories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Inventories</SelectItem>
                {inventories.map(inv => (
                  <SelectItem key={inv.id} value={inv.id}>{inv.inventory_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-12 rounded-lg border-gray-300 text-sm"
                data-testid="job-start-date"
              />
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-12 rounded-lg border-gray-300 text-sm"
                data-testid="job-end-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Jobs Table */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <Briefcase size={20} className="text-[#0776BD]" />
            Jobs ({sortedJobs.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
            </div>
          ) : sortedJobs.length === 0 ? (
            <div className="py-12 text-center text-[#6B7280]">
              <Briefcase size={48} className="mx-auto mb-4 opacity-50" />
              <p>No jobs found</p>
              <p className="text-sm">Jobs appear here when materials are used with a job number</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937] cursor-pointer hover:bg-gray-200" onClick={() => handleSort('job_number')}>
                      <div className="flex items-center gap-1">Job Number <SortIcon column="job_number" /></div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937] cursor-pointer hover:bg-gray-200" onClick={() => handleSort('last_date')}>
                      <div className="flex items-center gap-1">Date <SortIcon column="last_date" /></div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center cursor-pointer hover:bg-gray-200" onClick={() => handleSort('item_count')}>
                      <div className="flex items-center justify-center gap-1">Items Used <SortIcon column="item_count" /></div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('total_cost')}>
                      <div className="flex items-center justify-end gap-1">Material Cost <SortIcon column="total_cost" /></div>
                    </TableHead>
                    {taxRate > 0 && (
                      <TableHead className="font-semibold text-[#1F2937] text-right">Tax ({taxRate}%)</TableHead>
                    )}
                    {taxRate > 0 && (
                      <TableHead className="font-semibold text-[#1F2937] text-right">Total w/ Tax</TableHead>
                    )}
                    <TableHead className="font-semibold text-[#1F2937] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedJobs.map((job) => (
                    <TableRow key={job.job_number} className="hover:bg-[#F5F6F7]" data-testid={`job-row-${job.job_number}`}>
                      <TableCell>
                        <span className="font-medium text-[#0776BD]">{job.job_number}</span>
                      </TableCell>
                      <TableCell className="text-[#6B7280]">
                        <div>{new Date(job.first_date).toLocaleDateString()}</div>
                        {job.first_date !== job.last_date && (
                          <div className="text-xs">to {new Date(job.last_date).toLocaleDateString()}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-[#E6F2FA] text-[#0776BD] border-0">{job.item_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-bold text-[#1F2937] text-lg">
                        ${job.total_cost.toFixed(2)}
                      </TableCell>
                      {taxRate > 0 && (
                        <TableCell className="text-right text-[#6B7280]">
                          ${(job.total_cost * taxRate / 100).toFixed(2)}
                        </TableCell>
                      )}
                      {taxRate > 0 && (
                        <TableCell className="text-right font-bold text-[#0776BD] text-lg">
                          ${(job.total_cost * (1 + taxRate / 100)).toFixed(2)}
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(job)}
                          className="hover:bg-[#E6F2FA] text-[#0776BD] gap-1"
                          data-testid={`view-job-${job.job_number}`}
                        >
                          <Eye size={16} />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Job Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl rounded-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937] flex items-center gap-2">
              <Briefcase className="text-[#0776BD]" />
              Job: {selectedJob?.job_number}
            </DialogTitle>
          </DialogHeader>

          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
            </div>
          ) : jobDetail ? (
            <div className="space-y-4">
              <div className={`grid gap-4 ${taxRate > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                <div className="p-4 bg-[#E6F2FA] rounded-lg text-center">
                  <p className="text-sm text-[#6B7280]">Subtotal</p>
                  <p className="text-2xl font-bold text-[#0776BD]" data-testid="detail-total-cost">${jobDetail.total_cost.toFixed(2)}</p>
                </div>
                {taxRate > 0 && (
                  <div className="p-4 bg-orange-50 rounded-lg text-center">
                    <p className="text-sm text-[#6B7280]">Tax ({taxRate}%)</p>
                    <p className="text-2xl font-bold text-orange-600">${(jobDetail.total_cost * taxRate / 100).toFixed(2)}</p>
                  </div>
                )}
                <div className="p-4 bg-[#F5F6F7] rounded-lg text-center">
                  <p className="text-sm text-[#6B7280]">Items Used</p>
                  <p className="text-2xl font-bold text-[#1F2937]">{jobDetail.transaction_count}</p>
                </div>
                <div className="p-4 bg-[#F5F6F7] rounded-lg text-center">
                  <p className="text-sm text-[#6B7280]">{taxRate > 0 ? 'Total After Tax' : 'Date'}</p>
                  <p className={`font-bold text-[#1F2937] ${taxRate > 0 ? 'text-2xl' : 'text-sm'}`}>
                    {taxRate > 0
                      ? `$${(jobDetail.total_cost * (1 + taxRate / 100)).toFixed(2)}`
                      : (jobDetail.first_date ? new Date(jobDetail.first_date).toLocaleDateString() : '-')}
                  </p>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937]">Item</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Inventory</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center">Qty</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Unit Price</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Total</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobDetail.items.map((item, idx) => (
                    <TableRow key={item.transaction_id || `job-item-${idx}`}>
                      <TableCell>
                        <div className="font-medium text-[#1F2937]">{item.item_name}</div>
                        <div className="text-xs text-[#0776BD] font-mono">({item.internal_code})</div>
                      </TableCell>
                      <TableCell className="text-[#6B7280] text-sm">{item.inventory_name}</TableCell>
                      <TableCell className="text-center text-[#1F2937]">
                        {item.quantity}
                        {item.has_adjustments && (
                          <div className="text-xs text-amber-600">(orig: {item.original_quantity})</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-[#6B7280]">${item.unit_price.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium text-[#1F2937]">${item.total_cost.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openAdjust(item)}
                            className="hover:bg-amber-50 text-amber-600 h-7 px-2 text-xs gap-1"
                            data-testid={`adjust-btn-${idx}`}
                          >
                            <Edit3 size={12} /> Adjust
                          </Button>
                          {item.has_adjustments && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openHistory(item.transaction_id)}
                              className="hover:bg-gray-100 text-[#6B7280] h-7 px-2 text-xs gap-1"
                              data-testid={`history-btn-${idx}`}
                            >
                              <History size={12} /> Log
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 border-[#0776BD]">
                    <TableCell colSpan={4} className="text-right font-bold text-[#1F2937]">Subtotal</TableCell>
                    <TableCell className="text-right font-bold text-xl text-[#0776BD]">${jobDetail.total_cost.toFixed(2)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                  {taxRate > 0 && (
                    <>
                      <TableRow>
                        <TableCell colSpan={4} className="text-right font-medium text-[#6B7280]">Tax ({taxRate}%)</TableCell>
                        <TableCell className="text-right font-medium text-[#6B7280]">${(jobDetail.total_cost * taxRate / 100).toFixed(2)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={4} className="text-right font-bold text-[#1F2937]">Total After Tax</TableCell>
                        <TableCell className="text-right font-bold text-xl text-[#0776BD]">${(jobDetail.total_cost * (1 + taxRate / 100)).toFixed(2)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)} className="rounded-lg">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjustment Dialog */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
              <Edit3 className="text-amber-600" />
              Adjust Material Usage
            </DialogTitle>
          </DialogHeader>
          {adjustItem && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F5F6F7] rounded-lg">
                <p className="font-medium text-[#1F2937]">{adjustItem.item_name}</p>
                <p className="text-xs text-[#0776BD] font-mono">({adjustItem.internal_code})</p>
                <p className="text-sm text-[#6B7280] mt-1">Current quantity: <span className="font-bold">{adjustItem.quantity}</span></p>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1F2937] font-medium">New Quantity</Label>
                <Input
                  type="number"
                  min="0"
                  value={adjustQty}
                  onChange={(e) => setAdjustQty(e.target.value)}
                  className="h-11 rounded-lg border-gray-300"
                  data-testid="adjust-qty-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#1F2937] font-medium">Notes (optional)</Label>
                <Input
                  value={adjustNotes}
                  onChange={(e) => setAdjustNotes(e.target.value)}
                  placeholder="Reason for adjustment..."
                  className="h-11 rounded-lg border-gray-300"
                  data-testid="adjust-notes-input"
                />
              </div>
              {adjustQty && parseInt(adjustQty) !== adjustItem.quantity && (
                <div className={`p-3 rounded-lg text-sm ${parseInt(adjustQty) > adjustItem.quantity ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  {parseInt(adjustQty) > adjustItem.quantity
                    ? `Will subtract ${parseInt(adjustQty) - adjustItem.quantity} more from inventory`
                    : `Will return ${adjustItem.quantity - parseInt(adjustQty)} to inventory`}
                </div>
              )}
              {adjustError && <p className="text-red-500 text-sm" data-testid="adjust-error">{adjustError}</p>}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAdjustOpen(false)} className="rounded-lg">Cancel</Button>
            <Button
              onClick={submitAdjustment}
              disabled={adjusting}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg gap-2"
              data-testid="submit-adjustment-btn"
            >
              {adjusting ? <Loader2 size={16} className="animate-spin" /> : <Edit3 size={16} />}
              Apply Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjustment History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg rounded-xl max-h-[60vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
              <History className="text-[#6B7280]" />
              Adjustment History
            </DialogTitle>
          </DialogHeader>
          {loadingHistory ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#0776BD]" /></div>
          ) : adjustmentHistory.length === 0 ? (
            <p className="text-center text-[#6B7280] py-8">No adjustments recorded</p>
          ) : (
            <div className="space-y-3">
              {adjustmentHistory.map((adj, idx) => (
                <div key={adj.id || `adj-${idx}`} className="p-3 bg-[#F5F6F7] rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-[#1F2937]">{adj.notes}</p>
                      <p className="text-xs text-[#6B7280]">By: {adj.performed_by_name}</p>
                    </div>
                    <Badge className={adj.quantity > 0 ? "bg-green-100 text-green-700 border-0" : "bg-red-100 text-red-700 border-0"}>
                      {adj.quantity > 0 ? '+' : ''}{adj.quantity}
                    </Badge>
                  </div>
                  <p className="text-xs text-[#6B7280] mt-1">{new Date(adj.timestamp).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)} className="rounded-lg">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobSummary;
